import { Box, ErrorMessage, Flex, Text } from '@/components';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import type { AmountSearchConditionFieldName } from '@/features/search/_utils/project-amount-search';
import type { FC } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AmountSearchBudgetTypeField } from './AmountSearchBudgetTypeField';
import { AmountSearchFiscalYearField } from './AmountSearchFiscalYearField';
import { AmountSearchTypeField } from './AmountSearchTypeField';
import { AmountSearchValueField } from './AmountSearchValueField';
import { AmountSearchValueIsNullField } from './threshold/AmountSearchValueIsNullField';
import { TrendConditionField } from './trend/TrendConditionField';

type FiledName = FieldPath<ProjectSearchFormType>;

type Props = {
  index: number;
};

export const AnalyticSearchFields: FC<Props> = ({ index }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<ProjectSearchFormType>();
  const formulaConditionsName =
    `formulaConditions.${index}` satisfies FiledName;
  const conditionTypeName =
    `${formulaConditionsName}.conditionType` satisfies FiledName;
  const baseAmountYearFieldName =
    `${formulaConditionsName}.baseAmountYear` satisfies FiledName;
  const targetAmountYearFieldName =
    `${formulaConditionsName}.targetAmountYear` satisfies FiledName;
  const baseAmountTypeFieldName =
    `${formulaConditionsName}.baseAmountType` satisfies FiledName;
  const targetAmountTypeFieldName =
    `${formulaConditionsName}.targetAmountType` satisfies FiledName;
  const baseAmountValueFromFieldName =
    `${formulaConditionsName}.baseAmountValueFrom` satisfies FiledName;
  const baseAmountValueToFieldName =
    `${formulaConditionsName}.baseAmountValueTo` satisfies FiledName;

  const conditionType = useWatch({ control, name: conditionTypeName });

  return (
    <>
      <Flex gap="12px" wrap="wrap">
        {conditionType === 'threshold' ? (
          <>
            <AmountSearchTypeField
              name={conditionTypeName}
              hint={t('hint-search-threshold')}
              index={index}
            />
            <AmountSearchFiscalYearField
              name={baseAmountYearFieldName}
              index={index}
              type="baseAmountYear"
            />
            <AmountSearchBudgetTypeField
              conditionType={conditionType}
              name={baseAmountTypeFieldName}
              yearFieldName={baseAmountYearFieldName}
            />
            <Flex gap="8px">
              <AmountSearchValueField
                name={baseAmountValueFromFieldName}
                baseAmountTypeFieldName={baseAmountTypeFieldName}
                yearFieldName={baseAmountYearFieldName}
                label={t('search.min-amount')}
                index={index}
                conditionType={conditionType}
              />
              <Box marginTop="36px">
                <Text text="~" weight="regular" />
              </Box>
              <AmountSearchValueField
                name={baseAmountValueToFieldName}
                baseAmountTypeFieldName={baseAmountTypeFieldName}
                yearFieldName={baseAmountYearFieldName}
                label={t('search.max-amount')}
                index={index}
                conditionType={conditionType}
              />
            </Flex>
            <AmountSearchValueIsNullField index={index} />
          </>
        ) : (
          <>
            <AmountSearchTypeField
              name={conditionTypeName}
              hint={t('hint-search-trend')}
              index={index}
            />
            <AmountSearchFiscalYearField
              name={baseAmountYearFieldName}
              index={index}
              type="baseAmountYear"
            />
            <AmountSearchBudgetTypeField
              conditionType={conditionType}
              name={baseAmountTypeFieldName}
              yearFieldName={baseAmountYearFieldName}
            />
            <Box marginTop="34px">
              <Text text="→" weight="regular" />
            </Box>
            <AmountSearchFiscalYearField
              name={targetAmountYearFieldName}
              index={index}
              type="targetAmountYear"
            />
            <AmountSearchBudgetTypeField
              conditionType={conditionType}
              name={targetAmountTypeFieldName}
              yearFieldName={targetAmountYearFieldName}
            />
            <AmountSearchValueField
              name={`formulaConditions.${index}.trendConditionValue`}
              label={t('amount-search-value')}
              yearFieldName={baseAmountYearFieldName}
              targetAmountYearFieldName={targetAmountYearFieldName}
              index={index}
              conditionType={conditionType}
            />
            <TrendConditionField index={index} />
          </>
        )}
      </Flex>
      <ConditionTypeErrorMessage
        index={index}
        conditionTypeName={conditionTypeName}
      />
    </>
  );
};

const ConditionTypeErrorMessage: FC<{
  index: number;
  conditionTypeName: AmountSearchConditionFieldName<'conditionType'>;
}> = ({ index, conditionTypeName }) => {
  const {
    formState: { errors },
  } = useFormContext<ProjectSearchFormType>();
  if (!errors.formulaConditions?.[index]?.conditionType) {
    return null;
  }
  return (
    <Box marginBottom="8px">
      <ErrorMessage errors={errors} name={conditionTypeName} />
    </Box>
  );
};
